import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// graphql
import {
  POWER_BI_REPORTS_QUERY,
  POWER_BI_WORKSPACES_QUERY,
  POWER_BI_DATASET_REFRESHED_AT_QUERY
} from 'graphql/powerBI/queries'
import {
  POWER_BI_CLONE_REPORT_MUTATION,
  POWER_BI_DELETE_REPORT_MUTATION,
  POWER_BI_UPDATE_REPORT_MUTATION,
  POWER_BI_REFRESH_DATASET_MUTATION
} from 'graphql/powerBI/mutations'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// types
import { TRefetch } from 'types/refetch'
import {
  IPowerBiReport,
  IPowerBiWorkspace,
  IPowerBiReportsResponse,
  IPowerBiReportsQueryArgs,
  IPowerBiReportUpdateInput,
  IPowerBiDatasetRefreshedAtData,
  IPowerBiDatasetRefreshedAtResponse
} from 'types/graphql'
// utils
import get from 'lodash/get'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type Props = React.PropsWithChildren<unknown>

interface Context {
  cloneReport: (id: string, workspaceId: string) => void
  customReports: IPowerBiReport[]
  customWorkspace: IPowerBiWorkspace
  datasetRefreshedAt: IPowerBiDatasetRefreshedAtData
  deleteReport: (id: string) => void
  isDataLoading: boolean
  isLoading: boolean
  refetch: TRefetch<IPowerBiReportsResponse, IPowerBiReportsQueryArgs>
  refetchDatasetRefreshedAt: TRefetch<IPowerBiDatasetRefreshedAtResponse, void>
  refreshDatasets: () => void
  updateReport: (id: string, input: IPowerBiReportUpdateInput) => Promise<void>
  standardReports: IPowerBiReport[]
}

export const DataHubAPIContext = React.createContext<Context>({} as any)

const DataHubAPI: React.FC<Props> = ({ children }) => {
  const {
    data: powerBIReportsQuery,
    loading: isLoading,
    refetch
  } = useQuery(POWER_BI_REPORTS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {},
      pagination: {
        pageTake: 100,
        pageSkip: 0
      }
    }
  })

  const { data: powerBIDatasetRefreshedAtQuery } = useQuery(POWER_BI_DATASET_REFRESHED_AT_QUERY)
  const { data: powerBIWorkspacesQuery, refetch: refetchDatasetRefreshedAt } = useQuery(POWER_BI_WORKSPACES_QUERY)

  const [isDataLoading, setDataLoading] = React.useState<boolean>(false)

  const { client } = useApolloClient()

  // constants
  const powerBIReports: IPowerBiReport[] = get(powerBIReportsQuery, 'powerBIReports.data', [])
  const customReports = powerBIReports.filter(({ standard }) => !standard)
  const standardReports = powerBIReports.filter(({ standard }) => standard)

  const datasetRefreshedAt = get(powerBIDatasetRefreshedAtQuery, 'powerBIDatasetRefreshedAt.data', {})
  const customWorkspace: IPowerBiWorkspace = get(powerBIWorkspacesQuery, 'powerBIWorkspaces.data', []).find(
    ({ standard }) => !standard
  )

  // methods
  const cloneReport: Context['cloneReport'] = async (id, workspaceId) => {
    try {
      setDataLoading(true)

      await client.mutate({
        mutation: POWER_BI_CLONE_REPORT_MUTATION,
        variables: {
          input: {
            fromWorkspaceId: workspaceId,
            toWorkspaceId: customWorkspace.id,
            reportId: id
          }
        }
      })

      await refetch()
    } catch (e) {
      console.log('[DataHubAPI.cloneReport]: ', parseGraphQLErrorMessages(e))
    } finally {
      setDataLoading(false)
    }
  }

  const deleteReport: Context['deleteReport'] = async (id) => {
    try {
      await client.mutate({
        mutation: POWER_BI_DELETE_REPORT_MUTATION,
        variables: {
          id
        }
      })

      await refetch()
    } catch (e) {
      console.log('[DataHubAPI.deleteReport]: ', parseGraphQLErrorMessages(e))
    }
  }

  const refreshDatasets: Context['refreshDatasets'] = async () => {
    try {
      setDataLoading(true)
      await client.mutate({
        mutation: POWER_BI_REFRESH_DATASET_MUTATION
      })

      await refetchDatasetRefreshedAt()
    } catch (errors) {
      console.log('[DataHubAPI.refreshDatasets]: ', parseGraphQLErrorMessages(errors))
    } finally {
      setDataLoading(false)
    }
  }

  const updateReport: Context['updateReport'] = async (id, input) => {
    try {
      await client.mutate({
        mutation: POWER_BI_UPDATE_REPORT_MUTATION,
        variables: {
          id,
          input
        }
      })

      await refetch()
    } catch (e) {
      console.log('[DataHubAPI.updateReport]: ', parseGraphQLErrorMessages(e))
    }
  }

  const value = {
    cloneReport,
    customReports,
    customWorkspace,
    datasetRefreshedAt,
    deleteReport,
    isDataLoading,
    isLoading,
    refetch,
    refetchDatasetRefreshedAt,
    refreshDatasets,
    updateReport,
    standardReports
  } as any

  return <DataHubAPIContext.Provider value={value}>{children}</DataHubAPIContext.Provider>
}

export default DataHubAPI
